import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';

import { InputFieldDef, InputFieldType } from '../../../types/admin';
import {
  OUT_SEMANTICS,
  compactField,
  emptyField,
  parseDefaultValue,
  parseEnumInputs,
  parseOptionalJson,
  trimOrUndefined,
} from './inputFieldDialogHelpers';
import { DefaultValueInput } from './DefaultValueInput';

const FIELD_TYPES: InputFieldType[] = [
  'string',
  'integer',
  'number',
  'boolean',
  'enum',
  'object',
  'object[]',
  'media',
];
const CONTROLS = [
  'text',
  'textarea',
  'select',
  'slider',
  'number',
  'switch',
  'json',
  'image',
  'video',
  'audio',
];
const IN_SEMANTICS = [
  'none',
  'prompt',
  'negative_prompt',
  'duration_seconds',
  'aspect_ratio',
  'seed',
  'image',
  'images',
  'image_size',
  'output_format',
  'width',
  'height',
  'num_images',
  'steps',
];
/** Display text for semantics whose stored value alone is easy to miss. */
const SEMANTIC_LABELS: Record<string, string> = { image_size: 'image_size(resolution)' };
const INVALID_POLICIES = ['reject', 'clamp', 'snap'];
const DEFAULT_STATUS_MAP = {
  succeeded: ['succeeded'],
  running: ['running', 'queued'],
  failed: ['failed'],
};

interface InputFieldDialogProps {
  open: boolean;
  field?: InputFieldDef | null;
  /** Which side of the format is being edited; fixes `direction` and the semantic list. */
  direction: 'in' | 'out';
  onClose: () => void;
  onSave: (field: InputFieldDef) => void;
}

export const InputFieldDialog: React.FC<InputFieldDialogProps> = ({
  open,
  field,
  direction,
  onClose,
  onSave,
}) => {
  const isOut = direction === 'out';
  const [draft, setDraft] = React.useState<InputFieldDef>(emptyField);
  const [enumInput, setEnumInput] = React.useState('');
  const [wrapText, setWrapText] = React.useState('');
  const [childrenText, setChildrenText] = React.useState('');
  const [statusMapText, setStatusMapText] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const next = field ?? { ...emptyField, direction };
    setDraft(next);
    setWrapText(next.wrap === undefined ? '' : JSON.stringify(next.wrap, null, 2));
    setChildrenText(next.children === undefined ? '' : JSON.stringify(next.children, null, 2));
    setStatusMapText(next.statusMap ? JSON.stringify(next.statusMap, null, 2) : '');
    setErrors({});
    setEnumInput('');
  }, [field, open, direction]);

  const setValue = <K extends keyof InputFieldDef>(key: K, value: InputFieldDef[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const save = () => {
    const nextErrors: Record<string, string> = {};
    if (!draft.name.trim())
      nextErrors.name = isOut ? 'Response path is required' : 'Name is required';
    if (isOut && !draft.semantic) nextErrors.semantic = 'Pick what this response value means';
    if (draft.type === 'enum' && !isOut && (!draft.enum || draft.enum.length === 0)) {
      nextErrors.enum = 'Enum fields need at least one value';
    }

    const wrap = parseOptionalJson(wrapText, 'wrap', nextErrors);
    const children = parseOptionalJson(childrenText, 'children', nextErrors);
    const statusMap = parseOptionalJson(statusMapText, 'statusMap', nextErrors);
    const defaultValue = parseDefaultValue(draft.default, draft.type, nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    onSave(
      compactField({
        ...draft,
        name: draft.name.trim(),
        key: trimOrUndefined(draft.key),
        label: trimOrUndefined(draft.label),
        description: trimOrUndefined(draft.description),
        control: trimOrUndefined(draft.control),
        uiGroup: trimOrUndefined(draft.uiGroup),
        semantic: draft.semantic === 'none' ? undefined : trimOrUndefined(draft.semantic),
        direction,
        taskTypes: undefined,
        enum: draft.type === 'enum' ? draft.enum : undefined,
        wrap,
        children: Array.isArray(children) ? children : undefined,
        statusMap:
          isOut && draft.semantic === 'output.status' && statusMap && typeof statusMap === 'object'
            ? (statusMap as Record<string, string[]>)
            : undefined,
        default: defaultValue,
      })
    );
  };

  const title = `${field ? 'Edit' : 'Add'} ${isOut ? 'response field' : 'input field'}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextField
            required
            label={isOut ? 'Response path' : 'Name'}
            value={draft.name}
            onChange={(event) => setValue('name', event.target.value)}
            error={!!errors.name}
            helperText={
              errors.name ||
              (isOut
                ? 'Dotted path inside the provider response, e.g. task.content.url or data[0].url'
                : 'Wire path, dotted paths are allowed')
            }
          />
          {isOut ? (
            <TextField
              select
              required
              label="Meaning (semantic)"
              value={draft.semantic ?? ''}
              onChange={(event) => setValue('semantic', event.target.value)}
              error={!!errors.semantic}
              helperText={errors.semantic || 'The platform only understands this closed list'}
            >
              {OUT_SEMANTICS.map((semantic) => (
                <MenuItem key={semantic} value={semantic}>
                  {semantic}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Key"
              value={draft.key ?? ''}
              onChange={(event) => setValue('key', event.target.value)}
              helperText="Optional UI/API key"
            />
          )}
          <TextField
            label="Label"
            value={draft.label ?? ''}
            onChange={(event) => setValue('label', event.target.value)}
          />
          <TextField
            select
            label="Type"
            value={draft.type}
            onChange={(event) => setValue('type', event.target.value as InputFieldType)}
          >
            {FIELD_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className="md:col-span-2"
            label="Description"
            value={draft.description ?? ''}
            onChange={(event) => setValue('description', event.target.value)}
            multiline
            rows={2}
          />

          {isOut && draft.semantic === 'output.status' && (
            <TextField
              className="md:col-span-2"
              label="Status map JSON"
              value={statusMapText}
              onChange={(event) => setStatusMapText(event.target.value)}
              error={!!errors.statusMap}
              helperText={
                errors.statusMap ||
                'Canonical state -> provider status strings. Empty = {succeeded, running, failed} with the same words.'
              }
              placeholder={JSON.stringify(DEFAULT_STATUS_MAP)}
              multiline
              rows={4}
              inputProps={{ className: 'font-mono text-xs' }}
            />
          )}

          {!isOut && (
            <>
              <TextField
                select
                label="Control"
                value={draft.control ?? ''}
                onChange={(event) => setValue('control', event.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {CONTROLS.map((control) => (
                  <MenuItem key={control} value={control}>
                    {control}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Semantic"
                value={draft.semantic ?? 'none'}
                onChange={(event) => setValue('semantic', event.target.value)}
              >
                {IN_SEMANTICS.map((semantic) => (
                  <MenuItem key={semantic} value={semantic}>
                    {SEMANTIC_LABELS[semantic] ?? semantic}
                  </MenuItem>
                ))}
              </TextField>
              <DefaultValueInput
                field={draft}
                error={errors.default}
                onChange={(value) => setValue('default', value)}
              />
              {draft.type === 'enum' && (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={(draft.enum ?? []).map(String)}
                  inputValue={enumInput}
                  onInputChange={(_event, value) => setEnumInput(value)}
                  onChange={(_event, value) => {
                    setValue('enum', parseEnumInputs(value.map(String)));
                    setEnumInput('');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Enum Values"
                      onBlur={() => {
                        if (!enumInput.trim()) return;
                        setValue(
                          'enum',
                          parseEnumInputs([...(draft.enum ?? []).map(String), enumInput])
                        );
                        setEnumInput('');
                      }}
                      error={!!errors.enum}
                      helperText={
                        errors.enum ||
                        'Press Enter after each value, or paste "a" | "b" | "c" / a, b, c'
                      }
                    />
                  )}
                />
              )}
              {(draft.type === 'integer' || draft.type === 'number') && (
                <>
                  <TextField
                    type="number"
                    label="Min"
                    value={draft.min ?? ''}
                    onChange={(event) =>
                      setValue(
                        'min',
                        event.target.value === '' ? undefined : Number(event.target.value)
                      )
                    }
                  />
                  <TextField
                    type="number"
                    label="Max"
                    value={draft.max ?? ''}
                    onChange={(event) =>
                      setValue(
                        'max',
                        event.target.value === '' ? undefined : Number(event.target.value)
                      )
                    }
                  />
                </>
              )}
              {draft.type === 'string' && (
                <TextField
                  type="number"
                  label="Max Length"
                  value={draft.maxLength ?? ''}
                  onChange={(event) =>
                    setValue(
                      'maxLength',
                      event.target.value === '' ? undefined : Number(event.target.value)
                    )
                  }
                />
              )}
              <TextField
                label="UI Group"
                value={draft.uiGroup ?? ''}
                onChange={(event) => setValue('uiGroup', event.target.value)}
              />
              <TextField
                select
                label="On Invalid"
                value={draft.onInvalid ?? ''}
                onChange={(event) =>
                  setValue('onInvalid', event.target.value as InputFieldDef['onInvalid'])
                }
              >
                <MenuItem value="">Default</MenuItem>
                {INVALID_POLICIES.map((policy) => (
                  <MenuItem key={policy} value={policy}>
                    {policy}
                  </MenuItem>
                ))}
              </TextField>
              <Box className="flex flex-wrap gap-4 md:col-span-2">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draft.required === true}
                      onChange={(event) => setValue('required', event.target.checked)}
                    />
                  }
                  label="Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draft.hidden === true}
                      onChange={(event) => setValue('hidden', event.target.checked)}
                    />
                  }
                  label="Hidden"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draft.pricingDimension === true}
                      onChange={(event) => setValue('pricingDimension', event.target.checked)}
                    />
                  }
                  label="Price can depend on this field's value"
                />
              </Box>
              <Accordion className="md:col-span-2">
                <AccordionSummary expandIcon={<ChevronDown size={18} />}>
                  <Typography variant="subtitle2">Advanced</Typography>
                </AccordionSummary>
                <AccordionDetails className="grid grid-cols-1 gap-4">
                  <TextField
                    label="Wrap JSON"
                    value={wrapText}
                    onChange={(event) => setWrapText(event.target.value)}
                    error={!!errors.wrap}
                    helperText={errors.wrap || 'Empty = omitted'}
                    multiline
                    rows={4}
                  />
                  <TextField
                    label="Children JSON"
                    value={childrenText}
                    onChange={(event) => setChildrenText(event.target.value)}
                    error={!!errors.children}
                    helperText={errors.children || 'Empty = omitted'}
                    multiline
                    rows={4}
                  />
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>
          Save Field
        </Button>
      </DialogActions>
    </Dialog>
  );
};
